// Client-side merge of multiple institutes' Profile Analytics dashboards into one
// combined view (used by the "All Institutes" option for group/multi-campus admins).
// Each institute's dashboard is fetched individually (existing GET endpoint) and
// merged here — no backend changes required.
import {
    BreakupCompany,
    BreakupRow,
    BreakupSectionData,
    EngagementRatesData,
    EntrepreneurAnalysisData,
    GenerationRow,
    ProfileAnalyticsData,
    RevenueBracket,
    SeniorityFunnelData,
} from './types';
import { companyDisplayName, companyIdOf } from './directoryLinks';

const keyOf = (label: string) => label.trim().toLowerCase();
const isOthers = (name: string) => keyOf(name) === 'others';

const companyKey = (company: BreakupCompany) => keyOf(companyDisplayName(company));

const mergeCompany = (prev: BreakupCompany | undefined, next: BreakupCompany): BreakupCompany => {
    const name = companyDisplayName(next) || (prev ? companyDisplayName(prev) : '');
    const id = companyIdOf(next) ?? (prev ? companyIdOf(prev) : null);
    return id != null ? { name, id } : { name };
};

/**
 * Re-apply Industry / Functional Area chart rule on merged totals:
 * keep groups with pct > 7% (cap at top 10 by pct); roll the rest into "Others".
 * "Others" never gets topCompanies.
 */
const applyBreakupBucketing = (
    groups: Array<{ name: string; code: string | null; count: number; topCompanies: BreakupCompany[] }>,
    seedOthersCount: number
): BreakupSectionData => {
    const namedTotal = groups.reduce((sum, g) => sum + g.count, 0);
    const totalCount = namedTotal + seedOthersCount;
    const list = groups
        .map((g) => ({
            ...g,
            pct: totalCount > 0 ? g.count / totalCount : 0,
        }))
        .sort((a, b) => b.pct - a.pct);

    const above = list.filter((g) => g.pct > 0.07);
    const kept = above.length > 10 ? list.slice(0, 10) : above;
    const keptNames = new Set(kept.map((g) => g.name));
    const rolled = list.filter((g) => !keptNames.has(g.name));
    const othersCount = seedOthersCount + rolled.reduce((sum, g) => sum + g.count, 0);

    const rows: BreakupRow[] = kept.map((g) => ({
        name: g.name,
        code: g.code,
        count: g.count,
        pct: g.pct,
        topCompanies: g.topCompanies.slice(0, 4),
    }));
    if (othersCount > 0) {
        rows.push({
            name: 'Others',
            count: othersCount,
            pct: totalCount > 0 ? othersCount / totalCount : null,
            topCompanies: [],
        });
    }

    return { rows, total: { count: totalCount, pct: 1 } };
};

/** Merge Industry_Breakup / Functional_Area sections, then re-apply >7% / top-10 / Others. */
const mergeBreakup = (sections: Array<BreakupSectionData | null>): BreakupSectionData | null => {
    const valid = sections.filter((s): s is BreakupSectionData => !!s && s.rows.length > 0);
    if (valid.length === 0) return null;

    const map = new Map<
        string,
        { name: string; code: string | null; count: number; topCompanies: Map<string, BreakupCompany> }
    >();
    let seedOthersCount = 0;
    valid.forEach((section) => {
        section.rows.forEach((row: BreakupRow) => {
            if (isOthers(row.name)) {
                seedOthersCount += row.count || 0;
                return;
            }
            const key = keyOf(row.name);
            const existing = map.get(key);
            if (existing) {
                existing.count += row.count || 0;
                if (!existing.code && row.code) existing.code = row.code;
                row.topCompanies?.forEach((c) => {
                    const k = companyKey(c);
                    if (!k) return;
                    existing.topCompanies.set(k, mergeCompany(existing.topCompanies.get(k), c));
                });
            } else {
                const companies = new Map<string, BreakupCompany>();
                row.topCompanies?.forEach((c) => {
                    const k = companyKey(c);
                    if (!k) return;
                    companies.set(k, mergeCompany(undefined, c));
                });
                map.set(key, {
                    name: row.name,
                    code: row.code ?? null,
                    count: row.count || 0,
                    topCompanies: companies,
                });
            }
        });
    });

    const groups = Array.from(map.values()).map((g) => ({
        name: g.name,
        code: g.code,
        count: g.count,
        topCompanies: Array.from(g.topCompanies.values()),
    }));
    return applyBreakupBucketing(groups, seedOthersCount);
};

/** Merge Seniority_Funnel levels by label (order = first institute's order), re-derive pct/dropPct. */
const mergeSeniority = (sections: Array<SeniorityFunnelData | null>): SeniorityFunnelData | null => {
    const valid = sections.filter((s): s is SeniorityFunnelData => !!s && s.levels.length > 0);
    if (valid.length === 0) return null;

    const order: string[] = [];
    const map = new Map<string, { label: string; count: number; notes: string | null }>();
    valid.forEach((section) => {
        section.levels.forEach((lvl) => {
            const key = keyOf(lvl.label);
            const existing = map.get(key);
            if (existing) {
                existing.count += lvl.count || 0;
                existing.notes = existing.notes ?? lvl.notes;
            } else {
                map.set(key, { label: lvl.label, count: lvl.count || 0, notes: lvl.notes });
                order.push(key);
            }
        });
    });

    const raw = order.map((key) => map.get(key)!);
    const top = raw[0]?.count || 0;
    const levels = raw.map((lvl, idx) => {
        const prev = idx > 0 ? raw[idx - 1].count : null;
        return {
            label: lvl.label,
            count: lvl.count,
            notes: lvl.notes,
            pct: top > 0 ? lvl.count / top : null,
            dropPct: idx > 0 && prev !== null && prev > 0 ? 1 - lvl.count / prev : null,
        };
    });

    return { levels };
};

/** Sum a list of {label,count} rows across institutes, preserving first-seen order. */
const mergeLabelCountRows = <T extends { count: number }>(
    rowsPerInstitute: T[][],
    labelOf: (row: T) => string,
    build: (label: string, count: number, sample: T) => T
): T[] => {
    const order: string[] = [];
    const map = new Map<string, { count: number; sample: T }>();
    rowsPerInstitute.forEach((rows) => {
        rows.forEach((row) => {
            const key = keyOf(labelOf(row));
            const existing = map.get(key);
            if (existing) {
                existing.count += row.count || 0;
            } else {
                map.set(key, { count: row.count || 0, sample: row });
                order.push(key);
            }
        });
    });
    return order.map((key) => {
        const { count, sample } = map.get(key)!;
        return build(labelOf(sample), count, sample);
    });
};

/** Merge Entrepreneur_Analysis: funnel, revenue breakup, and generation split. */
const mergeEntrepreneur = (
    sections: Array<EntrepreneurAnalysisData | null>
): EntrepreneurAnalysisData | null => {
    const valid = sections.filter((s): s is EntrepreneurAnalysisData => !!s);
    if (valid.length === 0) return null;

    const funnel = mergeLabelCountRows(
        valid.map((s) => s.funnel),
        (r) => String(r.key || r.stage || ''),
        (label, count, sample) => ({ key: sample.key || label, stage: sample.stage, count, notes: sample.notes })
    );
    const revenueBreakup: RevenueBracket[] = mergeLabelCountRows(
        valid.map((s) => s.revenueBreakup),
        (r) => String(r.code || r.threshold || ''),
        (label, count) => ({ code: label, count })
    );
    const top15: GenerationRow[] = mergeLabelCountRows(
        valid.map((s) => s.generationSplit.top15),
        (r) => String(r.code || r.label || ''),
        (label, count) => ({ code: label, count })
    );
    const all: GenerationRow[] = mergeLabelCountRows(
        valid.map((s) => s.generationSplit.all),
        (r) => String(r.code || r.label || ''),
        (label, count) => ({ code: label, count })
    );

    return { funnel, revenueBreakup, generationSplit: { top15, all } };
};

/** Merge Engagement_Rates: sum counts, re-derive percentages, weighted-average duration. */
const mergeEngagement = (sections: Array<EngagementRatesData | null>): EngagementRatesData | null => {
    const valid = sections.filter((s): s is EngagementRatesData => !!s);
    if (valid.length === 0) return null;

    const totalAlumni = valid.reduce((sum, s) => sum + (s.totalAlumni || 0), 0);

    const sumCount = (pick: (s: EngagementRatesData) => number | null) =>
        valid.reduce((sum, s) => sum + (pick(s) || 0), 0);
    const pctOf = (count: number) => (totalAlumni > 0 ? count / totalAlumni : null);

    const engagedAnyCount = sumCount((s) => s.email.engagedAny.count);
    const engaged3PlusCount = sumCount((s) => s.email.engaged3Plus.count);
    const engaged7PlusCount = sumCount((s) => s.email.engaged7Plus.count);

    const descriptions = Array.from(
        new Set(valid.map((s) => s.email.campaignDescription).filter((d): d is string => !!d))
    );
    const channelTypes = Array.from(
        new Set(valid.map((s) => s.otherChannel.channelType).filter((c): c is string => !!c))
    );

    const durationWeightSum = valid.reduce(
        (sum, s) =>
            sum + (s.otherChannel.avgConversationDurationMins || 0) * (s.totalAlumni || 0),
        0
    );
    const avgConversationDurationMins =
        totalAlumni > 0 && durationWeightSum > 0
            ? Math.round((durationWeightSum / totalAlumni) * 10) / 10
            : null;

    return {
        totalAlumni,
        email: {
            campaignDescription: descriptions.length > 0 ? descriptions.join('; ') : null,
            engagedAny: { count: engagedAnyCount, pct: pctOf(engagedAnyCount) },
            engaged3Plus: { count: engaged3PlusCount, pct: pctOf(engaged3PlusCount) },
            engaged7Plus: { count: engaged7PlusCount, pct: pctOf(engaged7PlusCount) },
        },
        otherChannel: {
            channelType: channelTypes.length > 0 ? channelTypes.join('; ') : null,
            avgConversationDurationMins,
            deepWhatsappChats: sumCount((s) => s.otherChannel.deepWhatsappChats),
        },
    };
};

/**
 * Merge several institutes' dashboards into a single combined dashboard.
 * Institutes with no data (null) should be filtered out by the caller before calling this.
 */
export const mergeProfileAnalyticsData = (dataset: ProfileAnalyticsData[]): ProfileAnalyticsData | null => {
    if (dataset.length === 0) return null;

    return {
        version: 1,
        industryBreakup: mergeBreakup(dataset.map((d) => d.industryBreakup)),
        functionalArea: mergeBreakup(dataset.map((d) => d.functionalArea)),
        seniorityFunnel: mergeSeniority(dataset.map((d) => d.seniorityFunnel)),
        entrepreneurAnalysis: mergeEntrepreneur(dataset.map((d) => d.entrepreneurAnalysis)),
        engagementRates: mergeEngagement(dataset.map((d) => d.engagementRates)),
        meta: {
            fileName: `Merged from ${dataset.length} institute${dataset.length > 1 ? 's' : ''}`,
        },
    };
};
